import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import ReusableTabs from '../common/ReusableTabs';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import AllProducts from './AllProducts';
import { useLocation } from 'react-router-dom';
import { replacePercent20ToSpace } from '../../utils/StringRelated';
import { useLocale } from '../../contexts/LocaleProvider';
import { LanguageMapping } from '../../utils/LanguageMapping';
import { excludedDefinitionIds, Industrial_Sensors_Products_ID } from '../layout/Header/Product';
import ProductsBreadCrumbs from '../productPage/tableComponents/ProductsBreadCrumbs';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import { useResourceTypes } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { Box } from '@mui/material';
import TechnicalResources from '../automotivePage/TechnicalResources';
import { DetectedDefinition } from '../../schemas/DetectedDefinition';
import { getProductFieldFromPath } from '../productPage/ProductHome';
import {
    ProductGroupByDmDefinitionId,
    ProductGroupById,
} from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { useSearchHandler } from '../../utils/useSearchHandler';
import { useLoading } from '../../contexts/LoadingContext';
import { ReturnParameterValuesLite } from '../../schemas/ReturnParameterValuesLite';
import { useDefinitions } from '../../contexts/DefinitionsContext';
import TE_IMG from '../../static/all_product_image/TE_Products.png';
import { ProductsContent } from '../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { getDefinitionDisplayName } from '../../utils/DefinitionDisplay';
import { toUrl } from '@/utils/convertImagePath';

/**
 * 根據目前偵測到的 `productField` 產生麵包屑
 * - 固定以 `/browse/products/` 為根
 * - 若存在 `productField`，再加入自身一層
 *   - 顯示文字：`getDefinitionDisplayName(productField, language)`
 *   - URL： `/browse/products/<productField.name>/`
 */
function useProductBreadcrumbs(
    productField: DetectedDefinition | undefined,
    language: string,
    contentTree: ProductsContent
): { name: string; url: string }[] {
    const BASE = '/browse/products';

    return useMemo(() => {
        // 根結點
        const crumbs: { name: string; url: string }[] = [
            { name: contentTree.common.tabs.Products.title.content, url: `${BASE}/` },
        ];

        // 產品層級
        if (productField) {
            crumbs.push({
                name: getDefinitionDisplayName(productField, language),
                url: `${BASE}/${productField.name}/`, // 英文 slug
            });
        }

        return crumbs;
    }, [productField, language, contentTree]);
}

interface BrowseAllProductHomeProps {
    queryRawResults?: ReturnParameterValuesLite;
    searchInput?: string | undefined;
    customBannerMessage?: string | undefined;
    includeResourceLibrary: boolean;
    excludeProducts: boolean;
    shouldClearAllProps?: boolean;
    productGroupByDmDefinitionId: ProductGroupByDmDefinitionId;
    productGroupById: ProductGroupById;
    contentTree: ProductsContent;
}

const BrowseAllProductHome: FunctionComponent<BrowseAllProductHomeProps> = ({
    queryRawResults,
    searchInput,
    customBannerMessage,
    includeResourceLibrary,
    excludeProducts,
    shouldClearAllProps,
    productGroupByDmDefinitionId,
    productGroupById,
    contentTree,
}) => {
    const location = useLocation();

    const [propsState, setPropsState] = useState<BrowseAllProductHomeProps>({
        queryRawResults,
        searchInput,
        customBannerMessage,
        includeResourceLibrary,
        excludeProducts,
        productGroupByDmDefinitionId,
        productGroupById,
        contentTree,
    });

    const [productField, setProductField] = useState<DetectedDefinition>();
    const { definitionsMap, rawDefinitions: definitionData } = useDefinitions();
    // console.log('productField', productField);

    useEffect(() => {
        // 取得 productFieldString （使用 helper 函數）
        const productFieldString = getProductFieldFromPath(location.pathname);
        setProductField(definitionsMap.get(productFieldString)?.get(productFieldString));
    }, [location.pathname, definitionsMap]);

    const industrialSensorsProducts = Object.values(productGroupById).find(
        (p) => p?.id === Industrial_Sensors_Products_ID.toString()
    );
    // 先直接從 mapping 取得 productGroup
    let productGroup = productField
        ? productGroupByDmDefinitionId[productField.id.toString()]
        : undefined;

    // 只有當 searchInput 不是 undefined 且值不同時，才更新 state
    useEffect(() => {
        if (shouldClearAllProps) {
            setPropsState({
                searchInput: undefined,
                customBannerMessage: undefined,
                includeResourceLibrary: false,
                excludeProducts: false,
                productGroupByDmDefinitionId: productGroupByDmDefinitionId,
                productGroupById: productGroupById,
                contentTree: contentTree,
            });
        }
        if (searchInput !== undefined) {
            // 使用 functional update，取得前一次的 state
            setPropsState((prevState) => {
                // 如果 searchInput 沒有變化，回傳原 state
                if (JSON.stringify(searchInput) === JSON.stringify(prevState.searchInput)) {
                    return prevState;
                }
                // 否則更新整個 state
                return {
                    queryRawResults,
                    searchInput,
                    customBannerMessage,
                    includeResourceLibrary,
                    excludeProducts,
                    productGroupByDmDefinitionId,
                    productGroupById,
                    contentTree,
                };
            });
        }
    }, [
        queryRawResults,
        searchInput,
        customBannerMessage,
        includeResourceLibrary,
        excludeProducts,
        shouldClearAllProps,
        contentTree,
    ]);

    // 如果 state 有值就使用 state 的，否則回退使用傳入的 props
    const effectiveQueryRawResults = propsState.queryRawResults ?? queryRawResults;
    const effectiveSearchInput = propsState.searchInput ?? searchInput;
    const effectiveIncludeResourceLibrary =
        propsState.includeResourceLibrary ?? includeResourceLibrary;
    const effectiveExcludeProducts = propsState.excludeProducts ?? excludeProducts;

    const { locale } = useLocale();
    const language = LanguageMapping.get(locale) || 'ENGLISH';

    const { resourceTypeByKey } = useResourceTypes();
    const resourceTypeKeys = Object.keys(resourceTypeByKey).filter((key) => !key.includes('/'));

    const currentPathPrefix = `/browse/products`;
    const path = location.pathname;

    const cleanedPath = useMemo(() => {
        return replacePercent20ToSpace(path.replace(currentPathPrefix, ''));
    }, [path, currentPathPrefix]);

    const filteredSegments = useMemo(() => {
        const segments = cleanedPath.split('/').filter((segment) => segment.length > 0);
        return ['Products', ...segments];
    }, [cleanedPath]);

    const enrichedDefinitionData = useMemo(() => {
        if (!definitionData || !industrialSensorsProducts) return definitionData;

        // 找到 id 為 611 的 definition
        const targetDef = definitionData.find((d) => d.id === 611);
        if (!targetDef) return definitionData;

        // 建立要插入的新項目（符合 DetectedDefinition 結構）
        const newChild: DetectedDefinition = {
            nameBreadcrumb: '',
            nameBreadcrumbs: [],
            id: Industrial_Sensors_Products_ID,
            name: 'Industrial Sensors',
            displayName: industrialSensorsProducts?.name ?? 'Industrial Sensors',
            displayNames: [],
            childDefinitions: [],
            definitionImage: {
                url: toUrl(TE_IMG),
            },
        };

        // 複製一份定義避免改動原資料
        return definitionData.map((def) => {
            if (def.id !== 611) return def;
            const children = [...(def.childDefinitions ?? []), newChild];
            children.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

            return {
                ...def,
                childDefinitions: children,
            };
        });
    }, [definitionData, industrialSensorsProducts, locale]);

    const filteredDefinitions = useMemo(() => {
        if (!enrichedDefinitionData) return null;
        if (effectiveQueryRawResults) {
            // Check if detectedDefinitions exists and is not empty
            if (!effectiveQueryRawResults.detectedDefinitions?.length) {
                return enrichedDefinitionData;
            }
            const detectedIds = new Set(
                effectiveQueryRawResults.detectedDefinitions.map((d) => d.id)
            );
            return enrichedDefinitionData.filter((d) => detectedIds.has(d.id));
        }

        return enrichedDefinitionData;
    }, [enrichedDefinitionData, effectiveQueryRawResults]);

    // 用輔助函數建立 Tabs 的 labels 與 contents
    const buildTabs = () => {
        if (!enrichedDefinitionData) {
            return { labels: [], contents: [], keys: [] };
        }

        const labels: string[] = [];
        const contents: JSX.Element[] = [];
        const keys: string[] = [];

        // 如果沒有排除 Products，就加入 Products 頁籤
        if (!effectiveExcludeProducts) {
            labels.push(contentTree.common.tabs.Products.title.content);
            keys.push('Products');
            contents.push(
                <AllProducts
                    definitions={(filteredDefinitions || enrichedDefinitionData).filter(
                        (def) => !excludedDefinitionIds.includes(def.id)
                    )}
                    filteredSegments={filteredSegments}
                    language={language}
                    queryRawResults={effectiveQueryRawResults}
                    searchInput={effectiveSearchInput}
                    contentTree={contentTree}
                />
            );
        }

        // 如果需要包含 Resource Library，則加入 Resources 頁籤
        if (effectiveIncludeResourceLibrary) {
            labels.push(contentTree.common.tabs.Resources.title.content);
            keys.push('Resources');
            contents.push(
                <PrefilteredResourceLibraryItemsProvider
                    initialFilters={{
                        resourceTypeKeys: resourceTypeKeys,
                        ...(!effectiveSearchInput ? { productGroupKeys: [] } : {}),
                        searchTerm: effectiveSearchInput,
                    }}
                    itemsPerPage={10}
                    sortOrder="latest"
                >
                    <Box sx={{ backgroundColor: '#FAFAFA' }}>
                        <TechnicalResources backgroundColor={'white'} />
                    </Box>
                </PrefilteredResourceLibraryItemsProvider>
            );
        }

        return { labels, contents, keys };
    };

    const { labels: tabLabels, contents: tabContents, keys: tabKeys } = buildTabs();

    // 使用 useSearchHandler hook 取得 searchByKeyword function
    const { setIsLoading } = useLoading();
    const { searchByKeyword } = useSearchHandler({ setIsLoading });

    useEffect(() => {
        const newSearchParams = new URLSearchParams(window.location.search);
        if (
            newSearchParams.has('search') &&
            propsState.searchInput !== newSearchParams.get('search')
        ) {
            const searchParam = newSearchParams.get('search');
            if (searchParam) {
                // console.log("我進來searchByKeyword(searchParam)")
                searchByKeyword(searchParam);
            }
        }
    }, [propsState]);

    return (
        <ReusableTabs
            bannerImage={contentTree.browseAllProducts.pageTitleBackgroundImage.imageUrl}
            bannerTitle={
                effectiveSearchInput
                    ? `${contentTree.common.searchResultsPageTitle.content} – ${effectiveSearchInput}`
                    : queryRawResults?.message?.text ||
                      `${contentTree.browseAllProducts.pageTitle.content}`
            }
            bannerDescription={
                productGroup
                    ? productGroup.longBlurb
                    : `${contentTree.browseAllProducts.pageSubtitle.content}`
            }
            tabLabels={tabLabels}
            tabKeys={tabKeys}
            tabContents={tabContents}
            bannerFontStyle={styles.bannerFont}
            subBannerFontStyle={styles.bannerFont2}
            pageName={'browseAllProductHome'}
            showTabs={false}
            changeUrl={false}
            breadcrumbs={
                <ProductsBreadCrumbs
                    breadCrumbs={useProductBreadcrumbs(productField, language, contentTree)}
                />
            }
        />
    );
};

export default BrowseAllProductHome;
